import { useTranslation } from "react-i18next";

import classNames from "../../../utils/classNames";

export const QUOTE_STAGES = [
  { stage: 1, label: "Confirmed"           , color: "emerald" },
  { stage: 2, label: "Engine Condition"    , color: "blue"    },
  { stage: 3, label: "Body Condition"      , color: "cyan"    },
  { stage: 4, label: "Contact Details"     , color: "violet"  },
  { stage: 5, label: "Quote Generated"     , color: "amber"   },
  { stage: 6, label: "Personal Information", color: "rose"    },
  { stage: 7, label: "Payment Details"     , color: "teal"    },
];

export type QuoteStage = (typeof QUOTE_STAGES)[number]["stage"];
type StageColor = (typeof QUOTE_STAGES)[number]["color"];

export const quoteStageColorMap: Record<
  StageColor,
  { bg: string; text: string, border: string; ring: string, dot: string }
> = {
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", ring: "ring-emerald-200", dot: "bg-emerald-500" },
  blue: { bg: "bg-blue-50", text: "text-blue-700", border: "border-blue-200", ring: "ring-blue-200", dot: "bg-blue-500" },
  cyan: { bg: "bg-cyan-50", text: "text-cyan-700", border: "border-cyan-200", ring: "ring-cyan-200", dot: "bg-cyan-500" },
  violet: { bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", ring: "ring-violet-200", dot: "bg-violet-500" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200", ring: "ring-amber-200", dot: "bg-amber-500" },
  rose: { bg: "bg-rose-50", text: "text-rose-700", border: "border-rose-200", ring: "ring-rose-200", dot: "bg-rose-500" },
  teal: { bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-200", ring: "ring-teal-200", dot: "bg-teal-500" },
};

export default function QuoteStageFilter({
  setActiveQuoteStage,
  activeQuoteStage,
  totalQuoteStages,
  stageCounts = {},
} : {
  setActiveQuoteStage: (quoteStage: QuoteStage | null) => void;
  activeQuoteStage: QuoteStage | null;
  totalQuoteStages?: number;
  stageCounts?: Partial<Record<QuoteStage, number>>;
}) {
  const { t } = useTranslation("leads");
  const computedTotalQuoteStages = totalQuoteStages ?? Object.values(stageCounts).reduce<number>((sum, value) => sum + (value ?? 0), 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-5 py-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">{t("Filter by Quote Stage")}</h3>
        <span className="text-xs text-gray-500">{t("{{count}} across stages", { count: computedTotalQuoteStages })}</span>
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-7 gap-2">
        {QUOTE_STAGES.map((qs) => {
          const count = stageCounts[qs.stage] ?? 0;
          const isActive = activeQuoteStage === qs.stage;
          const qsColor = quoteStageColorMap[qs.color];

          return (
            <button
              key={qs.stage}
              type="button"
              onClick={() => setActiveQuoteStage(isActive ? null : qs.stage)}
              className={classNames(
                "relative rounded-lg border px-2 py-2 text-center transition-all hover:shadow-sm",
                isActive
                  ? `${qsColor.bg} ${qsColor.border} ring-2 ${qsColor.ring}`
                  : "border-gray-200 hover:border-gray-300"
              )}
            >
              <div className="flex items-center justify-center gap-1.5">
                <span className={classNames("h-2 w-2 rounded-full shrink-0", qsColor.dot)} />
                <span className={classNames(
                  "text-[10px] font-medium leading-tight truncate",
                  isActive ? qsColor.text : "text-gray-600"
                )}>
                  {t(qs.label)}
                </span>
              </div>
              <span className={classNames(
                "block text-lg font-bold mt-0.5",
                isActive ? qsColor.text : "text-gray-900"
              )}>
                {count}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  )
}